<template>
    <TransitionRoot
        as="template"
        :show="layoutStore.isSidebarOpen">
        <Dialog
            as="div"
            class="relative z-50 lg:hidden"
            @close="layoutStore.isSidebarOpen = false">
            <TransitionChild
                as="template"
                enter="transition-opacity ease-linear duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="transition-opacity ease-linear duration-300"
                leave-from="opacity-100"
                leave-to="opacity-0">
                <div class="fixed inset-0 bg-gray-900/80" />
            </TransitionChild>

            <div class="fixed inset-0 flex">
                <TransitionChild
                    as="template"
                    enter="transition ease-in-out duration-300 transform"
                    enter-from="-translate-x-full"
                    enter-to="translate-x-0"
                    leave="transition ease-in-out duration-300 transform"
                    leave-from="translate-x-0"
                    leave-to="-translate-x-full">
                    <DialogPanel class="relative mr-16 flex w-full max-w-xs flex-1">
                        <TransitionChild
                            as="template"
                            enter="ease-in-out duration-300"
                            enter-from="opacity-0"
                            enter-to="opacity-100"
                            leave="ease-in-out duration-300"
                            leave-from="opacity-100"
                            leave-to="opacity-0">
                            <div class="absolute left-full top-0 flex w-16 justify-center pt-5">
                                <button
                                    type="button"
                                    class="-m-2.5 p-2.5"
                                    @click="layoutStore.isSidebarOpen = false">
                                    <span class="sr-only">Close sidebar</span>

                                    <Icon
                                        name="heroicons:x-mark"
                                        class="h-6 w-6 text-white"
                                        aria-hidden="true" />
                                </button>
                            </div>
                        </TransitionChild>

                        <div
                            class="flex grow flex-col gap-y-5 overflow-y-auto bg-white dark:bg-gray-900 px-6 pb-4 border-r border-gray-200 dark:border-gray-700">
                            <div class="flex h-16 shrink-0 items-center">
                                <LogoImage />
                            </div>

                            <div class="lg:hidden border-b">
                                <ServiceSwitcher class="lg:hidden mb-3" />
                            </div>

                            <nav class="flex flex-1 flex-col">
                                <ul
                                    role="list"
                                    class="flex flex-1 flex-col gap-y-7">
                                    <li
                                        v-for="(item, index) in headerMenuFiltered"
                                        v-show="item.ability ? $can(item.ability[0], item.ability[1]) : true"
                                        :key="item.title">
                                        <Disclosure
                                            v-if="item.children && item.children.length > 0"
                                            v-slot="{ open, close }"
                                            as="div">
                                            <DisclosureButton
                                                class="w-full flex justify-between"
                                                :class="[parentPath(item.children) ? 'bg-gray-50 text-primary-600' : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800', 'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold']">
                                                {{ titles[index].name }}
                                                <Icon
                                                    name="heroicons:chevron-down"
                                                    class="ml-4 h-5 w-5 text-gray-400 transition-transform duration-150 ease-in-out"
                                                    :class="{'rotate-180': open}"
                                                    aria-hidden="true" />
                                            </DisclosureButton>
                                            <DisclosurePanel class="mt-2 space-y-2">
                                                <DisclosureButton
                                                    v-for="(child, childIndex) in item.children"
                                                    :key="child.title"
                                                    as="div"
                                                    class="block rounded-lg  text-sm/7 font-semibold text-gray-900 hover:bg-gray-50">
                                                    <NuxtLink
                                                        v-if="child.path"
                                                        class="block text-sm w-full leading-6 py-2 pl-6 pr-3 hover:text-primary-600 dark:hover:text-white"
                                                        :class="currentPath(child.path) ? 'text-primary' : 'text-gray-900 dark:text-gray-100'"
                                                        :to="(child.isCmsPath) ? '/' + child.path : useLocalePath()(child.path)"
                                                        @click="close(); layoutStore.isSidebarOpen = false">
                                                        {{ titles[index].children[childIndex].name }}
                                                    </NuxtLink>

                                                    <a
                                                        v-else-if="child.function"
                                                        href="#"
                                                        class="block text-sm w-full leading-6 py-2 pl-6 pr-3 hover:text-primary-600 dark:hover:text-white"
                                                        :class="currentPath(item.path) ? 'text-primary' : 'text-gray-900 dark:text-gray-100 '"
                                                        @click.prevent="child.function()"
                                                        @click="close(); layoutStore.isSidebarOpen = false">
                                                        {{ titles[index].children[childIndex].name }}
                                                    </a>
                                                </DisclosureButton>
                                            </DisclosurePanel>
                                        </Disclosure>
                                        <NuxtLink
                                            v-else-if="item.path"
                                            :class="[currentPath(item.path) ? 'bg-gray-50 text-primary-600' : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800', 'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold']"
                                            :to="(item.isCmsPath) ? '/' + item.path : useLocalePath()(item.path)"
                                            @click="layoutStore.isSidebarOpen = false">
                                            {{ titles[index].name }}
                                        </NuxtLink>
                                        <a
                                            v-else-if="item.function"
                                            href="#"
                                            :class="[currentPath(item.path) ? 'bg-gray-50 text-primary-600' : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800', 'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold']"
                                            @click.prevent="item.function"
                                            @click="layoutStore.isSidebarOpen = false">
                                            {{ titles[index].name }}
                                        </a>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </DialogPanel>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script setup>
import {useRoute} from "vue-router"
import {useLayoutStore} from "@/store/layout"

const layoutStore = useLayoutStore()
const route = useRoute()

const props = defineProps({
    headerMenuFiltered: ({
        type: Array,
        default() {
            return []
        }
    }),
    titles: {
        type: Array,
        default() {
            return []
        }
    }
})

const currentPath = (path) => {
    if (path === '/') {
        return route.path === path
    }
    return route.path === '/' + path
}

const parentPath = (children) => {
    return children?.some(child => currentPath(child.path))
}
</script>